// public/js/modules/rbac.js
// Synced with backend/utils/permissions.mjs (as of May 2026)

// Mirrors backend/utils/permissions.mjs. 'admin' is listed EXPLICITLY in every
// entry (no automatic "admin inherits superadmin" fallback), so the UI matches
// the server's now-explicit model. Server remains authoritative.
export const ALLOWED_ROLES = {
  // Map + Assets — viewers and higher may view
  listAssets: [
    'viewer', 'powerViewer', 'operator',
    'groupAdministrator', 'tenantAdministrator',
    'platformSupport', 'admin', 'superadmin'
  ],
  viewAssetDetail: [
    'viewer', 'powerViewer', 'operator',
    'groupAdministrator', 'tenantAdministrator',
    'platformSupport', 'admin', 'superadmin'
  ],
  // Asset writes — kept in sync with backend/utils/permissions.mjs so the UI can
  // hide create/edit/delete affordances (server remains authoritative).
  createAsset: ['operator', 'groupAdministrator', 'tenantAdministrator', 'platformSupport', 'admin', 'superadmin'],
  updateAsset: ['operator', 'groupAdministrator', 'tenantAdministrator', 'platformSupport', 'admin', 'superadmin'],
  deleteAsset: ['tenantAdministrator', 'platformSupport', 'admin', 'superadmin'],

  // Devices — power viewers, operators and up may view own-tenant devices and
  // pair them to own assets (server enforces the tenant boundary). No plain
  // viewers. Kept in sync with backend/utils/permissions.mjs.
  listDevices: [
    'powerViewer', 'operator', 'groupAdministrator',
    'tenantAdministrator', 'platformSupport', 'admin', 'superadmin'
  ],
  assignDevice: [
    'powerViewer', 'operator', 'groupAdministrator',
    'tenantAdministrator', 'platformSupport', 'admin', 'superadmin'
  ],
  deleteDevice: [
    'tenantAdministrator', 'platformSupport', 'admin', 'superadmin'
  ],
  // Granting a device to a tenant is platform-only.
  grantDevice: ['platformSupport', 'admin', 'superadmin'],

  // Asset files + photos (docs/asset-files.md). Upload/rename from operator up
  // (field staff attach a damage photo on the spot); deleting is stricter.
  manageAssetFiles: [
    'operator', 'groupAdministrator',
    'tenantAdministrator', 'platformSupport', 'admin', 'superadmin'
  ],
  deleteAssetFiles: ['tenantAdministrator', 'platformSupport', 'admin', 'superadmin'],
  manageDocTypes: ['tenantAdministrator', 'platformSupport', 'admin', 'superadmin'],
  manageDeviceConfig: ['tenantAdministrator', 'platformSupport', 'admin', 'superadmin'],

  // Tenants
  listTenants: [
    'tenantAdministrator', 'platformSupport', 'admin', 'superadmin'
  ],
  manageTenants: [
    'tenantAdministrator', 'platformSupport', 'admin', 'superadmin'
  ],
  // Creating a new tenant is platform-level only (not tenant admins).
  createTenant: ['platformSupport', 'admin', 'superadmin'],

  // Users-Admin
  listUsers: [
    'tenantAdministrator', 'platformSupport', 'admin', 'superadmin'
  ],
  // FULL user administration (any role incl. platform, account lifecycle) — platform only.
  manageUserGroups: ['platformSupport', 'admin', 'superadmin'],
  // Tenant-scoped user admin: reset/set password + assign non-platform roles for
  // OWN-tenant users. Scope + platform-role ceiling enforced server-side.
  manageTenantUsers: ['tenantAdministrator', 'platformSupport', 'admin', 'superadmin'],
  // Deliveries page: the tenant's own device orders + tracking. Mirrors the backend.
  viewDeliveries: ['tenantAdministrator', 'platformSupport', 'admin', 'superadmin'],
  manageUserTenants: [
    'tenantAdministrator', 'platformSupport', 'admin', 'superadmin'
  ],

  // Settings — almost all roles
  listSettings: [
    'viewer', 'powerViewer', 'operator',
    'groupAdministrator', 'tenantAdministrator',
    'platformSupport', 'admin', 'superadmin'
  ],

  // Extra for Map (used in navigation)
  viewMap: [
    'viewer', 'powerViewer', 'operator',
    'groupAdministrator', 'tenantAdministrator',
    'platformSupport', 'admin', 'superadmin'
  ],

  // POIs (Points of Interest)
  listPois: [
    'viewer', 'powerViewer', 'operator',
    'groupAdministrator', 'tenantAdministrator',
    'platformSupport', 'admin', 'superadmin'
  ],
  createPoi: ['operator', 'groupAdministrator', 'tenantAdministrator', 'platformSupport', 'admin', 'superadmin'],
  updatePoi: ['operator', 'groupAdministrator', 'tenantAdministrator', 'platformSupport', 'admin', 'superadmin'],
  deletePoi: ['tenantAdministrator', 'platformSupport', 'admin', 'superadmin'],
  managePoiTypes: ['tenantAdministrator', 'platformSupport', 'admin', 'superadmin'],
  managePublicPois: ['platformSupport', 'admin', 'superadmin'],

  // Feedback tracker: everyone reports; platform admins triage + manage roadmap.
  manageFeedback: ['platformSupport', 'admin', 'superadmin']
};

// Platform roles a tenant-scoped admin may never grant or manage (mirror of
// permissions.mjs PLATFORM_ROLES) — used to hide those options in the UI.
export const PLATFORM_ROLES = ['admin', 'superadmin', 'platformSupport'];

export function can(userGroups = [], permissionKey) {
  const allowed = ALLOWED_ROLES[permissionKey] || [];
  if (!userGroups || userGroups.length === 0) return false;
  return userGroups.some(g => allowed.includes(g));
}

// Platform-admin level for UI gating: superadmin OR admin (the former superadmin
// cohort). Use this for pages/actions that were previously superadmin-only, so
// 'admin' users keep seeing them. Do NOT use it for the grant-superadmin ceiling.
//
// NOTE it excludes platformSupport on purpose - it still gates billing
// (#subscription), announcement authoring and several settings tabs. For an
// internal-operations page that support staff DO need, use isPlatformLevel.
export function isAdminLevel(userGroups = []) {
  return (userGroups || []).some(g => g === 'superadmin' || g === 'admin');
}

// Any platform role, INCLUDING platformSupport - the UI counterpart of the
// backend's `isSuperAdmin` (utils/middleware/auth.mjs), which resolves to the
// same three groups. Use it wherever the server gate is `superOnly(isSuperAdmin)`,
// so the page a role may actually call is also the page it can see: device
// distribution and the internal bench manual.
export function isPlatformLevel(userGroups = []) {
  return (userGroups || []).some(g => PLATFORM_ROLES.includes(g));
}

export function getCurrentUser() {
  try {
    return JSON.parse(localStorage.getItem('superb_tracking_user') || '{}');
  } catch {
    return {};
  }
}

// ── Add-on entitlements (docs/data-export.md) ────────────────────────────────
// These are NOT role-based — they come from the tenant's paid add-ons, resolved
// server-side and returned in the /settings payload. Cached in localStorage for
// UI gating only; the server re-checks on every export.
const ENTITLEMENTS_KEY = 'superb_tracking_entitlements';

export function setEntitlements(ent) {
  try { localStorage.setItem(ENTITLEMENTS_KEY, JSON.stringify(ent || {})); } catch {}
}

export function getEntitlements() {
  try { return JSON.parse(localStorage.getItem(ENTITLEMENTS_KEY) || '{}'); }
  catch { return {}; }
}

// May the current user export data anywhere (show/hide the Reports feature)?
export function canExport() {
  return getEntitlements().dataExport === true;
}

// Does the current user's tenant hold the API add-on (show/hide API settings)?
export function canUseApi() {
  return getEntitlements().api === true;
}